import React, { useEffect, useRef, useState } from 'react';
import { MdContentCopy, MdCheck, MdThumbUp, MdThumbDown } from 'react-icons/md';
import Button, { BUTTON_VARIANT_TEXT, BUTTON_VARIANT_PRIMARY } from '@components/1-atoms/Button';
import TextArea from '@components/1-atoms/TextArea';
import type { Message } from '@hooks/useChat';
import {
  ActionsBarWrapper,
  FeedbackPanel,
  FeedbackActions,
  IconButton,
  ThumbButton,
} from './ActionsBar.styled';

interface ActionsBarProps {
  message: Message;
  onSubmitFeedback: (messageId: string, rating: 'up' | 'down', comment?: string, fullContent?: string) => Promise<void>;
  onDeleteFeedback: (messageId: string) => Promise<void>;
}

type PendingRating = 'up' | 'down' | null;

const ActionsBar: React.FC<ActionsBarProps> = ({ message, onSubmitFeedback, onDeleteFeedback }) => {
  const [copied, setCopied] = useState(false);
  const [pendingRating, setPendingRating] = useState<PendingRating>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Clear copy timeout on unmount to avoid dangling timers
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    };
  }, []);

  // Close the feedback panel when clicking outside
  useEffect(() => {
    if (pendingRating === null) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setPendingRating(null);
        setComment('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pendingRating]);

  const handleCopy = () => {
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    navigator.clipboard.writeText(message.content).then(() => {
      setCopied(true);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    }).catch((err) => console.warn('Failed to copy to clipboard', err));
  };

  const handleThumbClick = (rating: 'up' | 'down') => {
    if (message.feedbackRating === rating && pendingRating === null) {
      onDeleteFeedback(message.id).catch((err) => console.warn('Failed to delete feedback', err));
      return;
    }
    setPendingRating(rating);
    setComment('');
  };

  const handleCancel = () => {
    setPendingRating(null);
    setComment('');
  };

  const handleSend = async () => {
    if (!pendingRating || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmitFeedback(message.id, pendingRating, comment.trim() || undefined, message.content);
      setPendingRating(null);
      setComment('');
    } catch (err) {
      console.error('Failed to submit feedback', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submittedRating = message.feedbackRating;

  return (
    <ActionsBarWrapper ref={wrapperRef}>
      <IconButton onClick={handleCopy} title="Copy response" aria-label="Copy response">
        {copied ? <MdCheck size={14} /> : <MdContentCopy size={14} />}
      </IconButton>

      <ThumbButton
        onClick={() => handleThumbClick('up')}
        $active={submittedRating === 'up' && pendingRating === null}
        title="Good response"
        aria-label="Good response"
      >
        <MdThumbUp size={14} />
      </ThumbButton>

      <ThumbButton
        onClick={() => handleThumbClick('down')}
        $active={submittedRating === 'down' && pendingRating === null}
        title="Bad response"
        aria-label="Bad response"
      >
        <MdThumbDown size={14} />
      </ThumbButton>

      {pendingRating !== null && (
        <FeedbackPanel ref={panelRef}>
          <TextArea
            placeholder="Add a comment"
            value={comment}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
            rows={3}
            autoFocus
          />
          <FeedbackActions>
            <Button variant={BUTTON_VARIANT_TEXT} stretch={false} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant={BUTTON_VARIANT_PRIMARY}
              stretch={false}
              onClick={handleSend}
              disabled={isSubmitting}
            >
              Send
            </Button>
          </FeedbackActions>
        </FeedbackPanel>
      )}
    </ActionsBarWrapper>
  );
};

export default ActionsBar;
